import React, { useState, useEffect, useRef } from "react";
import { X, Save, Plus, Target, Calendar, Flag, Folder } from "lucide-react";
import { toast } from "react-hot-toast";
import { createMilestone, updateMilestone } from "../../api/milestoneApi";

const KPIFormModal = ({ isOpen, onClose, projectId, milestone, onMilestoneAdded, onMilestoneUpdated }) => {
    // Reference to the modal's inner container
    const modalRef = useRef();

    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [formData, setFormData] = useState({
        milestoneTitle: '',
        dueDate: '',
        status: 'To-Do',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveAndAdd, setSaveAndAdd] = useState(false);
    const today = getTodayDate();

    // Populate the form with milestone data if provided (for editing)
    useEffect(() => {
        if (milestone) {
            setFormData({
                milestoneTitle: milestone.title,
                dueDate: new Date(milestone.dueDate).toISOString().split('T')[0],
                status: milestone.status,
            });
        } else {
            // For adding new milestones
            setFormData({
                milestoneTitle: '',
                dueDate: today,
                status: 'To-Do',
            });
        }
    }, [milestone, today]);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const milestoneData = {
                title: formData.milestoneTitle,
                dueDate: formData.dueDate,
                status: formData.status,
            };

            if (milestone) {
                await updateMilestone(milestone._id, milestoneData);
                toast.success("KPI updated successfully!");
                onMilestoneUpdated();
            } else {
                await createMilestone(projectId, milestoneData);
                toast.success("KPI saved successfully!");
                onMilestoneAdded();
            }

            if (saveAndAdd && !milestone) {
                setFormData({
                    milestoneTitle: '',
                    dueDate: today,
                    status: 'To-Do',
                });
            } else {
                onClose();
            }
        } catch (error) {
            toast.error(error.message || `Failed to ${milestone ? 'update' : 'save'} KPI.`);
            console.error(error);
        } finally {
            setIsSaving(false);
            setSaveAndAdd(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
            <div
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out"
            >
                {/* Header with Orange Theme */}
                <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500">
                    <div className="flex justify-between items-center">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                <Target className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-black">
                                    {milestone ? 'Edit KPI' : 'Add New KPI'}
                                </h2>
                                <div className="flex items-center mt-1 text-black text-sm">
                                    <Folder className="w-3 h-3 mr-1" />
                                    <span>Programs Department</span>
                                </div>
                            </div>
                        </div>
                        <button 
                            onClick={onClose} 
                            className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Form Body */}
                <div className="p-4 sm:p-6">
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                        {/* KPI Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                KPI Title <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Target className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="milestoneTitle"
                                    name="milestoneTitle"
                                    value={formData.milestoneTitle}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter KPI title"
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                />
                            </div>
                        </div>

                        {/* Due Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Due Date <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Calendar className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="date"
                                    id="dueDate"
                                    name="dueDate"
                                    value={formData.dueDate}
                                    onChange={handleChange}
                                    min={today}
                                    required
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                />
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Flag className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                >
                                    <option value="To-Do">To-Do</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Completed">Completed</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                            {!milestone && (
                                <button
                                    type="submit"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSaveAndAdd(true);
                                    }}
                                    disabled={isSaving}
                                    className="px-4 py-2.5 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 flex items-center justify-center space-x-2 order-2 sm:order-1"
                                >
                                    {isSaving ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={16} />
                                            <span>Save & Add Another</span>
                                        </>
                                    )}
                                </button>
                            )}
                            <button
                                type="submit"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSaveAndAdd(false);
                                }}
                                disabled={isSaving}
                                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2 order-1 sm:order-2"
                            >
                                {isSaving ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        <span>{milestone ? 'Update KPI' : 'Save KPI'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default KPIFormModal;